import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Search, Filter, Copy, Eye,
  ChevronDown, ChevronUp, FileText, Clock, Target, User, X, RefreshCw,
  BookOpen, Shuffle, FileSpreadsheet, Loader2, Brain, Edit3, Trophy, Upload, Loader
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MATERIAL_DELETE_PATHS = {
  study_guide: 'study-guides',
  flashcards: 'flashcards',
  matching: 'matching-games',
  memory: 'memory-games',
  fill_in: 'fill-ins',
  jeopardy: 'jeopardy-games',
};

const TIERS = [
  { value: 'All', label: 'All Tiers' },
  { value: 'basic', label: 'Basic Practice' },
  { value: 'comps_ready', label: 'Comps Ready' },
  { value: 'advanced', label: 'Advanced' }
];

const TIER_LABELS = { basic: 'Basic Practice', comps_ready: 'Comps Ready', advanced: 'Advanced' };

export default function TestBankManager() {
  const [tests, setTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [allCategories, setAllCategories] = useState([]);
  const [categoriesByTier, setCategoriesByTier] = useState({
    basic: [],
    comps_ready: [],
    advanced: []
  });
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [tierFilter, setTierFilter] = useState('All');
  const [sortBy, setSortBy] = useState('date_desc'); // date_desc, date_asc, name_asc, name_desc
  const [expandedTest, setExpandedTest] = useState(() => new URLSearchParams(window.location.search).get('highlightTest') || null);
  const [highlightId] = useState(() => new URLSearchParams(window.location.search).get('highlightTest'));
  const [testMaterials, setTestMaterials] = useState({});
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [generatingStudyMaterial, setGeneratingStudyMaterial] = useState({
    flashcards: null,
    matching: null,
    studyGuide: null,
    memoryGame: null,
    fillIn: null,
    jeopardy: null
  });
  
  const [showTestForm, setShowTestForm] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [showUploadStudyGuideModal, setShowUploadStudyGuideModal] = useState(false);
  const [uploadingStudyGuide, setUploadingStudyGuide] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [testForm, setTestForm] = useState({
    title: '',
    description: '',
    tier: 'basic',
    category: 'acupuncture',
    question_count: 10,
    passing_score: 70,
    time_limit_minutes: 0  // 0 means no time limit
  });

  // Helper function to get category label
  const getCategoryLabel = (tier, category) => {
    const tierCategories = categoriesByTier[tier] || [];
    const found = tierCategories.find(cat => cat.value === category);
    return found ? found.label : category;
  };

  useEffect(() => {
    loadTests();
    loadCategories();
  }, []);

  // Scroll to a deep-linked test once tests are loaded (no state change here)
  useEffect(() => {
    if (highlightId && tests.some(t => t.id === highlightId)) {
      const timer = setTimeout(() => {
        const el = document.getElementById(`test-row-${highlightId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [highlightId, tests]);

  const fetchTestMaterials = useCallback(async (testId) => {
    setLoadingMaterials(true);
    try {
      const res = await axios.get(`${API}/admin/tests/${testId}/study-materials`, { withCredentials: true });
      setTestMaterials(prev => ({ ...prev, [testId]: res.data.items || [] }));
    } catch (e) {
      console.warn('Failed to load study materials', e);
    } finally {
      setLoadingMaterials(false);
    }
  }, []);

  // Load study materials whenever a test is expanded
  useEffect(() => {
    if (expandedTest) fetchTestMaterials(expandedTest);
  }, [expandedTest, fetchTestMaterials]);

  const handleDeleteMaterial = async (testId, type, id, label) => {
    if (!window.confirm(`Delete this ${label}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/${MATERIAL_DELETE_PATHS[type]}/${id}`, { withCredentials: true });
      toast.success(`${label} deleted`);
      fetchTestMaterials(testId);
    } catch (e) {
      toast.error('Failed to delete study material');
    }
  };

  useEffect(() => {
    filterAndSortTests();
  }, [tests, searchTerm, categoryFilter, tierFilter, sortBy]);
  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/test-categories`, {
        withCredentials: true
      });
      setAllCategories(response.data);
      
      // Organize categories by tier
      const byTier = {
        basic: [],
        comps_ready: [],
        advanced: []
      };
      
      response.data.forEach(cat => {
        if (cat.enabled && byTier[cat.tier]) {
          byTier[cat.tier].push({
            value: cat.slug,
            label: cat.name
          });
        }
      });
      
      setCategoriesByTier(byTier);
    } catch (error) {
      console.error('Failed to load categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const loadTests = async () => {
    try {
      const response = await axios.get(`${API}/tests`);
      setTests(response.data);
    } catch (error) {
      toast.error('Failed to load tests');
    }
  };

  const filterAndSortTests = () => {
    let filtered = [...tests];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(test =>
        test.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        test.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter !== 'All') {
      filtered = filtered.filter(test => test.category?.toLowerCase() === categoryFilter.toLowerCase());
    }

    // Tier filter
    if (tierFilter !== 'All') {
      filtered = filtered.filter(test => test.tier === tierFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'date_asc':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'name_asc':
          return a.title.localeCompare(b.title);
        case 'name_desc':
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    });

    setFilteredTests(filtered);
  };

  const handleCreateTest = async (e) => {
    e.preventDefault();
    
    // Convert minutes to seconds, null if 0 (no time limit)
    const time_limit_seconds = testForm.time_limit_minutes > 0 
      ? testForm.time_limit_minutes * 60 
      : null;

    const testData = {
      ...testForm,
      time_limit_seconds
    };
    // Remove time_limit_minutes from the data sent to backend
    delete testData.time_limit_minutes;

    try {
      if (editingTest) {
        await axios.put(`${API}/admin/tests/${editingTest.id}`, testData, {
          withCredentials: true
        });
        toast.success('Test updated!');
      } else {
        await axios.post(`${API}/admin/tests`, testData, {
          withCredentials: true
        });
        toast.success('Test created!');
      }
      setShowTestForm(false);
      setEditingTest(null);
      setTestForm({ title: '', description: '', tier: 'basic', category: 'acupuncture', question_count: 10, passing_score: 70, time_limit_minutes: 0 });
      loadTests();
    } catch (error) {
      toast.error(`Failed to ${editingTest ? 'update' : 'create'} test`);
    }
  };

  const handleEditTest = (test) => {
    setEditingTest(test);
    setTestForm({
      title: test.title,
      description: test.description || '',
      tier: test.tier,
      category: test.category || 'acupuncture',
      question_count: test.question_count,
      passing_score: test.passing_score,
      time_limit_minutes: test.time_limit_seconds ? Math.round(test.time_limit_seconds / 60) : 0
    });
    setShowTestForm(true);
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Delete this test? This will also delete all questions and attempts.')) return;
    
    try {
      await axios.delete(`${API}/admin/tests/${testId}`, {
        withCredentials: true
      });
      toast.success('Test deleted');
      loadTests();
    } catch (error) {
      toast.error('Failed to delete test');
    }
  };

  const handleDuplicateTest = async (test) => {
    try {
      const duplicateData = {
        title: `${test.title} (Copy)`,
        description: test.description,
        tier: test.tier,
        category: test.category,
        question_count: test.question_count,
        passing_score: test.passing_score,
        time_limit_seconds: test.time_limit_seconds
      };
      await axios.post(`${API}/admin/tests`, duplicateData, {
        withCredentials: true
      });
      toast.success('Test duplicated! Add questions in Question Bank.');
      loadTests();
    } catch (error) {
      toast.error('Failed to duplicate test');
    }
  };

  // AI Study Material Generation Handlers
  const handleGenerateFlashcards = async (testId) => {
    setGeneratingStudyMaterial(prev => ({ ...prev, flashcards: testId }));
    try {
      const response = await axios.post(`${API}/admin/generate-flashcards-from-test/${testId}`, {}, {
        withCredentials: true
      });
      toast.success(`${response.data.card_count} flashcards generated successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate flashcards');
    } finally {
      setGeneratingStudyMaterial(prev => ({ ...prev, flashcards: null }));
      fetchTestMaterials(testId);
    }
  };

  const handleGenerateMatching = async (testId) => {
    setGeneratingStudyMaterial(prev => ({ ...prev, matching: testId }));
    try {
      const response = await axios.post(`${API}/admin/generate-matching-from-test/${testId}`, {}, {
        withCredentials: true
      });
      toast.success(`Matching game with ${response.data.pair_count} pairs generated successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate matching game');
    } finally {
      setGeneratingStudyMaterial(prev => ({ ...prev, matching: null }));
      fetchTestMaterials(testId);
    }
  };

  const handleGenerateStudyGuide = async (testId) => {
    setGeneratingStudyMaterial(prev => ({ ...prev, studyGuide: testId }));
    try {
      const response = await axios.post(`${API}/admin/generate-study-guide-from-test/${testId}`, {}, {
        withCredentials: true
      });
      toast.success(`Study guide with ${response.data.section_count} sections generated successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate study guide');
    } finally {
      setGeneratingStudyMaterial(prev => ({ ...prev, studyGuide: null }));
      fetchTestMaterials(testId);
    }
  };

  const handleGenerateMemoryGame = async (testId) => {
    setGeneratingStudyMaterial(prev => ({ ...prev, memoryGame: testId }));
    try {
      const response = await axios.post(`${API}/admin/generate-memory-game-from-test/${testId}`, {}, {
        withCredentials: true
      });
      toast.success(`Memory game with ${response.data.card_count} pairs generated successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate memory game');
    } finally {
      setGeneratingStudyMaterial(prev => ({ ...prev, memoryGame: null }));
      fetchTestMaterials(testId);
    }
  };

  const handleGenerateFillIn = async (testId) => {
    setGeneratingStudyMaterial(prev => ({ ...prev, fillIn: testId }));
    try {
      const response = await axios.post(`${API}/admin/generate-fill-in-from-test/${testId}`, {}, {
        withCredentials: true
      });
      toast.success(`Fill In with ${response.data.question_count} questions generated successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate Fill In');
    } finally {
      setGeneratingStudyMaterial(prev => ({ ...prev, fillIn: null }));
      fetchTestMaterials(testId);
    }
  };

  const handleGenerateJeopardy = async (testId) => {
    setGeneratingStudyMaterial(prev => ({ ...prev, jeopardy: testId }));
    try {
      const response = await axios.post(`${API}/admin/generate-jeopardy-from-test/${testId}`, {}, {
        withCredentials: true
      });
      toast.success(`Jeopardy game with ${response.data.question_count} questions generated successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate Jeopardy game');
    } finally {
      setGeneratingStudyMaterial(prev => ({ ...prev, jeopardy: null }));
      fetchTestMaterials(testId);
    }
  };


  const handleUploadStudyGuidePdf = async ({ title, tier, category, file }) => {
    setUploadingStudyGuide(true);
    setUploadProgress('Uploading PDF...');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      formData.append('tier', tier);
      formData.append('category', category);

      const startRes = await axios.post(`${API}/admin/study-guides/upload-pdf/start`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const jobId = startRes.data.job_id;

      setUploadProgress('Rendering pages...');
      let attempts = 0;
      while (attempts < 60) {
        await new Promise(r => setTimeout(r, 2000));
        const statusRes = await axios.get(`${API}/admin/study-guides/upload-pdf/status/${jobId}`, { withCredentials: true });
        if (statusRes.data.status === 'completed') {
          toast.success(`Study guide uploaded! ${statusRes.data.page_count} pages rendered.`);
          setShowUploadStudyGuideModal(false);
          break;
        } else if (statusRes.data.status === 'failed') {
          toast.error(statusRes.data.error || 'PDF processing failed');
          break;
        }
        attempts++;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setUploadingStudyGuide(false);
      setUploadProgress('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl retro-title text-[#2C1810]">Test Bank</h2>
        <div className="flex gap-3">
          <button
            onClick={() => setShowUploadStudyGuideModal(true)}
            className="retro-button-small flex items-center gap-2"
            title="Manually upload a ready-made PDF as a Study Guide"
            data-testid="upload-study-guide-pdf-button"
          >
            <Upload className="w-4 h-4" />
            Upload PDF Study Guide
          </button>
          <button
            onClick={async () => {
              try {
                const response = await axios.post(`${API}/admin/sync-test-question-counts`, {}, {
                  withCredentials: true
                });
                toast.success(response.data.message);
                loadTests();
              } catch (error) {
                toast.error('Failed to sync question counts');
              }
            }}
            className="retro-button-small flex items-center gap-2"
            title="Update question counts for all tests to match actual questions"
          >
            <RefreshCw className="w-4 h-4" />
            Sync Counts
          </button>
          <button
            onClick={() => {
              setEditingTest(null);
              setTestForm({ title: '', description: '', tier: 'basic', category: 'acupuncture', question_count: 10, passing_score: 70 });
              setShowTestForm(true);
            }}
            className="retro-button flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create New Test
          </button>
        </div>
      </div>

      {showUploadStudyGuideModal && (
        <UploadStudyGuidePdfModal
          tiers={TIERS.filter(t => t.value !== 'All')}
          categories={allCategories}
          uploading={uploadingStudyGuide}
          progress={uploadProgress}
          onClose={() => !uploadingStudyGuide && setShowUploadStudyGuideModal(false)}
          onSubmit={handleUploadStudyGuidePdf}
        />
      )}

      {/* Filters and Search */}
      <div className="cassette-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-[#6B4423] mb-2">
              <Search className="w-4 h-4 inline mr-1" />
              Search Tests
            </label>
            <input
              type="text"
              placeholder="Search by title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#6B4423] mb-2">
              <Filter className="w-4 h-4 inline mr-1" />
              Section
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
            >
              <option value="All">All Sections</option>
              <optgroup label="Basic">
                {categoriesByTier.basic?.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </optgroup>
              <optgroup label="Comps Ready">
                {categoriesByTier.comps_ready?.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </optgroup>
              <optgroup label="Advanced">
                {categoriesByTier.advanced?.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </optgroup>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#6B4423] mb-2">Tier</label>
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
            >
              {TIERS.map(tier => (
                <option key={tier.value} value={tier.value}>{tier.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#6B4423] mb-2">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
            >
              <option value="date_desc">Newest First</option>
              <option value="date_asc">Oldest First</option>
              <option value="name_asc">Name A-Z</option>
              <option value="name_desc">Name Z-A</option>
            </select>
          </div>
        </div>

        <div className="mt-4 text-sm text-[#6B4423]">
          Showing {filteredTests.length} of {tests.length} tests
        </div>
      </div>

      {/* Tests List */}
      <div className="space-y-4">
        {filteredTests.length === 0 ? (
          <div className="cassette-card p-12 text-center">
            <FileText className="w-16 h-16 text-[#6B4423] mx-auto mb-4" />
            <p className="text-[#3D2817] text-xl">No tests found</p>
            <p className="text-[#6B4423] mt-2">Create your first test to get started!</p>
          </div>
        ) : (
          filteredTests.map(test => (
            <div
              key={test.id}
              id={`test-row-${test.id}`}
              data-testid={`test-row-${test.id}`}
              className={`cassette-card transition-all ${highlightId === test.id ? 'ring-4 ring-[#4D9E7C] ring-offset-2' : ''}`}
            >
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl retro-title text-[#2C1810]">{test.title}</h3>
                      <span className="px-3 py-1 bg-[#E8934D] text-white text-xs font-bold rounded">
                        {TIER_LABELS[test.tier] || test.tier}
                      </span>
                      <span className="px-3 py-1 bg-[#4D9E7C] text-white text-xs font-bold rounded">
                        {getCategoryLabel(test.tier, test.category)}
                      </span>
                    </div>
                    {test.description && (
                      <p className="text-sm text-[#6B4423] mb-3">{test.description}</p>
                    )}
                    
                    {/* Metadata */}
                    <div className="flex flex-wrap gap-4 text-xs text-[#6B4423]">
                      <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        <span>{test.question_count} questions</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Target className="w-4 h-4" />
                        <span>{test.passing_score}% passing</span>
                      </div>
                      {test.time_limit_seconds && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{Math.round(test.time_limit_seconds / 60)} min</span>
                        </div>
                      )}
                      {test.created_at && (
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          <span>Created {new Date(test.created_at).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Action Buttons - Wrap properly on mobile */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => window.location.href = `/admin/quiz-management?testId=${test.id}`}
                        className="retro-button-small flex items-center gap-1"
                        title="Manage Questions"
                      >
                        <FileText className="w-4 h-4" />
                        Questions
                      </button>
                      
                      <button
                        onClick={() => handleEditTest(test)}
                        className="retro-button-small flex items-center gap-1"
                        title="Edit Test"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                      
                      <button
                        onClick={() => handleDeleteTest(test.id)}
                        className="retro-button-small flex items-center gap-1 bg-red-600 hover:bg-red-700"
                        title="Delete Test"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                    
                    {/* AI Study Material Generation - Compact Grid */}
                    <div>
                      <p className="text-xs font-semibold text-[#6B4423] mb-2">AI Generate Study Materials:</p>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      <button
                        onClick={() => handleGenerateFlashcards(test.id)}
                        disabled={generatingStudyMaterial.flashcards === test.id}
                        className="px-2 py-1.5 text-xs rounded bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 flex flex-col items-center gap-1"
                        title="AI Generate Flashcards"
                      >
                        {generatingStudyMaterial.flashcards === test.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <BookOpen className="w-4 h-4" />
                        )}
                        <span>Flash</span>
                      </button>
                      <button
                        onClick={() => handleGenerateMatching(test.id)}
                        disabled={generatingStudyMaterial.matching === test.id}
                        className="px-2 py-1.5 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 flex flex-col items-center gap-1"
                        title="AI Generate Matching Game"
                      >
                        {generatingStudyMaterial.matching === test.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Shuffle className="w-4 h-4" />
                        )}
                        <span>Match</span>
                      </button>
                      <button
                        onClick={() => handleGenerateStudyGuide(test.id)}
                        disabled={generatingStudyMaterial.studyGuide === test.id}
                        className="px-2 py-1.5 text-xs rounded bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 flex flex-col items-center gap-1"
                        title="AI Generate Study Guide"
                      >
                        {generatingStudyMaterial.studyGuide === test.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <FileSpreadsheet className="w-4 h-4" />
                        )}
                        <span>Guide</span>
                      </button>
                      <button
                        onClick={() => handleGenerateMemoryGame(test.id)}
                        disabled={generatingStudyMaterial.memoryGame === test.id}
                        className="px-2 py-1.5 text-xs rounded bg-pink-600 hover:bg-pink-700 text-white disabled:opacity-50 flex flex-col items-center gap-1"
                        title="AI Generate Memory Game"
                      >
                        {generatingStudyMaterial.memoryGame === test.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Brain className="w-4 h-4" />
                        )}
                        <span>Memory</span>
                      </button>
                      <button
                        onClick={() => handleGenerateFillIn(test.id)}
                        disabled={generatingStudyMaterial.fillIn === test.id}
                        className="px-2 py-1.5 text-xs rounded bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50 flex flex-col items-center gap-1"
                        title="AI Generate Fill In"
                      >
                        {generatingStudyMaterial.fillIn === test.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Edit3 className="w-4 h-4" />
                        )}
                        <span>Fill In</span>
                      </button>
                      <button
                        onClick={() => handleGenerateJeopardy(test.id)}
                        disabled={generatingStudyMaterial.jeopardy === test.id}
                        className="px-2 py-1.5 text-xs rounded bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50 flex flex-col items-center gap-1"
                        title="AI Generate Jeopardy Game"
                      >
                        {generatingStudyMaterial.jeopardy === test.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trophy className="w-4 h-4" />
                        )}
                        <span>Jeopardy</span>
                      </button>
                    </div>
                  </div>
                  </div>
                    
                  <div className="flex gap-2">
                    <button
                      onClick={() => setExpandedTest(expandedTest === test.id ? null : test.id)}
                      className="p-2 hover:bg-[#E8B67A] rounded"
                      title="View Details"
                    >
                      {expandedTest === test.id ? (
                        <ChevronUp className="w-5 h-5 text-[#2C1810]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#2C1810]" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEditTest(test)}
                      className="p-2 hover:bg-[#E8B67A] rounded"
                      title="Edit Test"
                    >
                      <Edit className="w-5 h-5 text-[#2C1810]" />
                    </button>
                    <button
                      onClick={() => handleDuplicateTest(test)}
                      className="p-2 hover:bg-[#E8B67A] rounded"
                      title="Duplicate Test"
                    >
                      <Copy className="w-5 h-5 text-[#2C1810]" />
                    </button>
                    <button
                      onClick={() => handleDeleteTest(test.id)}
                      className="p-2 hover:bg-red-200 rounded"
                      title="Delete Test"
                    >
                      <Trash2 className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedTest === test.id && (
                  <div className="mt-4 pt-4 border-t-2 border-[#E8B67A]">
                    <h4 className="text-sm font-bold text-[#2C1810] mb-2">Test Details:</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-[#6B4423]">ID:</span>
                        <p className="text-[#2C1810] font-mono text-xs">{test.id}</p>
                      </div>
                      <div>
                        <span className="text-[#6B4423]">Questions:</span>
                        <p className="text-[#2C1810]">{test.question_count}</p>
                      </div>
                      <div>
                        <span className="text-[#6B4423]">Passing Score:</span>
                        <p className="text-[#2C1810]">{test.passing_score}%</p>
                      </div>
                      <div>
                        <span className="text-[#6B4423]">Time Limit:</span>
                        <p className="text-[#2C1810]">
                          {test.time_limit_seconds ? `${Math.round(test.time_limit_seconds / 60)} minutes` : 'Unlimited'}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-[#6B4423] mt-3">
                      💡 Go to Question Bank to add or manage questions for this test
                    </p>

                    {/* Created Study Materials - manage / delete duplicates */}
                    <div className="mt-4 pt-3 border-t border-[#E8B67A]">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-bold text-[#2C1810]">Created Study Materials:</h4>
                        <button
                          onClick={() => fetchTestMaterials(test.id)}
                          className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1"
                          data-testid={`refresh-materials-${test.id}`}
                        >
                          <RefreshCw className="w-3 h-3" /> Refresh
                        </button>
                      </div>
                      {loadingMaterials && !testMaterials[test.id] ? (
                        <p className="text-xs text-[#6B4423]">Loading…</p>
                      ) : (testMaterials[test.id] || []).length === 0 ? (
                        <p className="text-xs text-[#6B4423]">No study materials created from this test yet.</p>
                      ) : (
                        <div className="space-y-1.5" data-testid={`materials-list-${test.id}`}>
                          {(testMaterials[test.id] || []).map((m) => {
                            const dupCount = (testMaterials[test.id] || []).filter(x => x.type === m.type).length;
                            return (
                              <div
                                key={`${m.type}-${m.id}`}
                                data-testid={`material-${m.id}`}
                                className="flex items-center justify-between bg-[#F4E4C1] border border-[#8B6F47] rounded px-3 py-2"
                              >
                                <div className="min-w-0">
                                  <span className="text-xs font-bold text-[#2C1810]">{m.type_label}</span>
                                  {dupCount > 1 && (
                                    <span className="ml-2 text-[10px] font-bold text-amber-800 bg-amber-200 rounded-full px-2 py-0.5">duplicate</span>
                                  )}
                                  <span className="text-xs text-[#6B4423] ml-2">
                                    {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                                  </span>
                                </div>
                                <button
                                  onClick={() => handleDeleteMaterial(test.id, m.type, m.id, m.type_label)}
                                  data-testid={`delete-material-${m.id}`}
                                  className="text-red-600 hover:bg-red-100 rounded p-1.5 flex-shrink-0"
                                  title={`Delete this ${m.type_label}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Test Modal */}
      {showTestForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="cassette-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl retro-title text-[#2C1810]">
                {editingTest ? 'Edit Test' : 'Create New Test'}
              </h3>
              <button onClick={() => setShowTestForm(false)} className="text-[#2C1810]">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreateTest} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Test Title *</label>
                <input
                  type="text"
                  value={testForm.title}
                  onChange={(e) => setTestForm({ ...testForm, title: e.target.value })}
                  required
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Description</label>
                <textarea
                  value={testForm.description}
                  onChange={(e) => setTestForm({ ...testForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Tier *</label>
                  <select
                    value={testForm.tier}
                    onChange={(e) => {
                      const newTier = e.target.value;
                      const firstCategory = categoriesByTier[newTier]?.[0]?.value || '';
                      setTestForm({ ...testForm, tier: newTier, category: firstCategory });
                    }}
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  >
                    <option value="basic">Basic Practice</option>
                    <option value="comps_ready">Comps Ready</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-[#6B4423]">Section *</label>
                    <button
                      type="button"
                      onClick={loadCategories}
                      className="text-xs text-[#E8934D] hover:text-[#E85D4D] flex items-center gap-1"
                      title="Refresh categories list"
                    >
                      <RefreshCw className="w-3 h-3" />
                      Refresh
                    </button>
                  </div>
                  <select
                    value={testForm.category}
                    onChange={(e) => setTestForm({ ...testForm, category: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  >
                    {categoriesByTier[testForm.tier]?.length > 0 ? (
                      categoriesByTier[testForm.tier].map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))
                    ) : (
                      <option value="">No categories available - Create one first</option>
                    )}
                  </select>
                  {categoriesByTier[testForm.tier]?.length === 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      No test categories exist for this tier. Create categories in the Test Categories tab first.
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Question Count *</label>
                  <input
                    type="number"
                    value={testForm.question_count}
                    onChange={(e) => setTestForm({ ...testForm, question_count: parseInt(e.target.value) })}
                    min="1"
                    required
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Passing Score (%) *</label>
                  <input
                    type="number"
                    value={testForm.passing_score}
                    onChange={(e) => setTestForm({ ...testForm, passing_score: parseInt(e.target.value) })}
                    min="0"
                    max="100"
                    required
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">
                  Time Limit (minutes) 
                  <span className="text-sm font-normal ml-2 text-[#8B5A3C]">(0 = no time limit)</span>
                </label>
                <input
                  type="number"
                  value={testForm.time_limit_minutes}
                  onChange={(e) => setTestForm({ ...testForm, time_limit_minutes: parseInt(e.target.value) || 0 })}
                  min="0"
                  placeholder="Enter time limit in minutes (e.g., 30, 45, 60)"
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                />
              </div>

              <div className="flex gap-3">
                <button type="submit" className="retro-button flex-1">
                  {editingTest ? 'Update Test' : 'Create Test'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowTestForm(false)}
                  className="retro-button-small flex-1 bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadStudyGuidePdfModal({ tiers, categories, uploading, progress, onClose, onSubmit }) {
  const [title, setTitle] = useState('');
  const [tier, setTier] = useState(tiers[0]?.value || 'basic');
  const [category, setCategory] = useState('');
  const [file, setFile] = useState(null);

  const availableCategories = categories.filter(c => c.tier === tier);

  const handleSubmit = () => {
    if (!title.trim() || !tier || !category || !file) {
      toast.error('Please fill in all fields and choose a PDF file');
      return;
    }
    onSubmit({ title, tier, category, file });
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="upload-study-guide-modal">
      <div className="bg-white rounded-lg max-w-lg w-full p-6 relative">
        <button
          onClick={onClose}
          disabled={uploading}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 disabled:opacity-40"
          data-testid="close-upload-study-guide-modal"
        >
          <X className="w-6 h-6" />
        </button>
        <h2 className="text-2xl retro-title text-[#2C1810] mb-6">Upload PDF Study Guide</h2>

        {uploading ? (
          <div className="text-center py-10">
            <Loader className="w-10 h-10 animate-spin text-[#E8934D] mx-auto mb-4" />
            <p className="text-[#6B4423] font-semibold">{progress || 'Processing...'}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-[#2C1810] mb-1">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full border-2 border-[#D4A574] rounded-lg px-3 py-2 focus:outline-none focus:border-[#E8934D]"
                placeholder="e.g., Meridian Pathways Study Guide"
                data-testid="study-guide-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#2C1810] mb-1">Tier</label>
              <select
                value={tier}
                onChange={(e) => { setTier(e.target.value); setCategory(''); }}
                className="w-full border-2 border-[#D4A574] rounded-lg px-3 py-2 focus:outline-none focus:border-[#E8934D]"
                data-testid="study-guide-tier-select"
              >
                {tiers.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-[#2C1810] mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full border-2 border-[#D4A574] rounded-lg px-3 py-2 focus:outline-none focus:border-[#E8934D]"
                data-testid="study-guide-category-select"
              >
                <option value="">Select a category...</option>
                {availableCategories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-[#2C1810] mb-1">PDF File</label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full border-2 border-[#D4A574] rounded-lg px-3 py-2"
                data-testid="study-guide-file-input"
              />
            </div>
            <button
              onClick={handleSubmit}
              className="w-full retro-button mt-2"
              data-testid="submit-study-guide-upload-button"
            >
              Upload & Process
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
